import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import type { PartyGameCard } from "../types/api";
import type { PartyShellPalette } from "../theme/theme";
import { PartyMascot } from "./PartyMascot";
import { PartyPlayFormatChips } from "./PartyPlayFormatChips";
import { PartyTvCastGlyph } from "./PartyTvCastGlyph";

const TvChip = styled("span")({
  display: "inline-flex",
  padding: "2px 6px",
  lineHeight: 0,
});

const Root = styled(Box)(({ theme }) => ({
  color: "var(--ink)",
  fontFamily: "inherit",
  textAlign: "start",
  width: "100%",
  position: "relative",
  borderRadius: "var(--r-card)",
  overflow: "hidden",
  isolation: "isolate",
  padding: theme.spacing(1.25),
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: 132,
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1.5),
    minHeight: 152,
  },
}));

export function PartyShellModeCardPreview({
  game,
  index,
  compact = true,
}: {
  game: PartyGameCard;
  /** 1-based display index (e.g. grid order). */
  index: number;
  /** Tighter padding and mascot for dense layouts (onboarding, tooltips). */
  compact?: boolean;
}) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const p = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const mascotSize = compact ? 52 : 72;
  const titleSize = compact ? 15 : 22;
  const displayName = (i18n.language || "").startsWith("ar") ? game.nameAr : game.nameEn;

  return (
    <Root
      className="party-kan-card party-anim-card-deal"
      style={{ ["--card-bg" as string]: game.bg } as CSSProperties}
      aria-label={displayName}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75} sx={{ width: "100%" }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography
            component="span"
            className="party-mono"
            sx={{ fontSize: 9, color: p.inkDim, letterSpacing: "0.08em", flexShrink: 0 }}
          >
            #{String(index).padStart(2, "0")}
          </Typography>
          <TvChip className="party-chip">
            <PartyTvCastGlyph support={game.supportsTvCast} />
          </TvChip>
        </Stack>
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "center", py: compact ? 0.75 : 1.25 }}>
        <PartyMascot shellId={game.id} size={mascotSize} />
      </Box>

      <Box>
        <Typography
          component="div"
          className="party-display"
          sx={{
            fontSize: titleSize,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {displayName}
        </Typography>
        <Typography
          sx={{
            fontSize: 9,
            color: p.inkDim,
            mt: 0.35,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {game.tag}
        </Typography>
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={0.5}
          sx={{
            mt: 0.5,
            "& .party-chip": { fontSize: `${compact ? 8 : 10}px !important` },
          }}
        >
          <Typography component="span" className="party-chip" sx={{ fontSize: 9, fontWeight: 700 }}>
            {game.playersTag}
          </Typography>
          <PartyPlayFormatChips format={game.playFormat} />
        </Stack>
      </Box>
    </Root>
  );
}
